import axios from 'axios';
import { axiosCloudConfig } from '../api/config';
import mainTypes from '../constants/ActionTypes/Main';
import { API_CLOUD_EZLO_REQUEST_URL } from '../constants/URLs';
import { bugsnagNotifyWrapper } from '../containers/ErrorBoundary/utils';
import { sleep } from './sleep';
import oemManager from 'services/oem';
import { compressData, decompressData } from 'lib/pako';

function getDataByKeyFromLS(key) {
    return JSON.parse(localStorage.getItem(key));
}
function setDataToLS(key, data) {
    return localStorage.setItem(key, JSON.stringify(data));
}

export function clearLSFromRedundantData() {
    const allLocalStorageKeys = Object.keys(localStorage);
    const allEnvs = Object.keys(oemManager.appConfig.getEnvironments());

    allEnvs.forEach((envKey) => {
        if (allLocalStorageKeys.includes(envKey)) {
            localStorage.removeItem(envKey);
        }
    });
}

export async function getIntegrationsData(params) {
    const currentEnv = localStorage.getItem(mainTypes.CLOUD_ENVIRONMENT);
    const storage = getDataByKeyFromLS(mainTypes.INTEGRATIONS_STORAGE);

    if (!storage || currentEnv !== storage.env) {
        clearLSFromRedundantData();
        setDataToLS(mainTypes.INTEGRATIONS_STORAGE, { env: currentEnv });
    }

    return extractCachedDataAndUpdate(params, mainTypes.INTEGRATIONS_STORAGE);
}

async function extractCachedDataAndUpdate(params, key) {
    const storage = getDataByKeyFromLS(key);
    const { call: method } = params;

    if (storage?.[method] && storage?.version) {
        fetchAndSaveDataToCache(params, key);

        return decompressData(storage[method]);
    }

    return fetchAndSaveDataToCache(params, key);
}

export const fetchAndSaveDataToCache = async (params, storageKey) => {
    const { call: method } = params;

    const buildRequestParams = {
        method: 'post',
        data: params,
        ...axiosCloudConfig(),
    };

    try {
        const response = await fetchRetry(buildRequestParams);
        const { status, data } = (await response?.data) || {};

        if (status !== mainTypes.SUCCESS_STATUS) {
            throw data;
        }

        const keys = Object.keys(data);
        const key = keys[mainTypes.KEY_INDEX];

        setDataToCache(data[key], method, storageKey);

        return data[key];
    } catch (error) {
        bugsnagNotifyWrapper(error);
    }
};

export const setDataToCache = (data, method, storageKey) => {
    const storage = getDataByKeyFromLS(storageKey);
    storage[method] = compressData(data);
    storage.version = 1;
    setDataToLS(storageKey, storage);
};

const fetchRetry = async (
    fetchParams,
    { limit = mainTypes.DEFAULT_LIMIT_COUNT, delay = mainTypes.DEFAULT_TIMEOUT, ...opts } = {},
) => {
    async function onError(err) {
        const shouldRetry = limit > mainTypes.MIN_LIMIT_COUNT;
        if (!shouldRetry) {
            throw err;
        }

        await sleep(delay);

        return fetchRetry(fetchParams, { limit: limit - mainTypes.STEP_DECREASING_LIMIT, delay, ...opts });
    }

    return axios({
        url: API_CLOUD_EZLO_REQUEST_URL(),
        ...fetchParams,
    })
        .then(async (response) => {
            const status = response?.data?.status;

            if (status !== mainTypes.SUCCESS_STATUS) {
                throw response.data;
            }

            return response;
        })
        .catch(onError);
};
